// =======================================================
// UPDATED FILE WITH SOURCE1 & SOURCE2 PREVIEW BUTTONS AND MATCH TYPE VIEWS
// =======================================================

import { useState, useEffect, useRef } from "react";
import * as XLSX from "xlsx";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { Dialog } from "primereact/dialog";
import { TabView, TabPanel } from "primereact/tabview";
import fetchMethodRequest from "../../../../config/service";
import showToasterMessage from '../../../UI/ToasterMessage/toasterMessage.jsx';

export default function PaymentDocumentation() {
  const toast = useRef(null);
  const invoicesFileInputRef = useRef(null);
  const settlementsFileInputRef = useRef(null);

  const initialRow = {};

  const [apiData, setApiData] = useState([initialRow]);
  const [apiColumns, setApiColumns] = useState(Object.keys(initialRow));

  const [showHeadingDialog, setShowHeadingDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [showMatchTypeDialog, setShowMatchTypeDialog] = useState(false);
  const [invoiceFileUploaded, setInvoiceFileUploaded] = useState(false);
  const [settlementFileUploaded, setSettlementFileUploaded] = useState(false);

  const [previewData, setPreviewData] = useState([]);
  const [previewColumns, setPreviewColumns] = useState([]);
  const [previewTitle, setPreviewTitle] = useState("");

  const [selectedFile, setSelectedFile] = useState(null);

  const [previousData, setPreviousData] = useState([]);
  const [currentData, setCurrentData] = useState([]);

  const [filePath, setFilePath] = useState("");

  useEffect(() => {
    fetchMatchedData();
  }, [])

  // State for different match types
  const [matchData, setMatchData] = useState({
    matched_1_1: [],
    matched_1_many: [],
    matched_many_1: [],
    matched_many_many: [],
    missing_in_settlement: [],
    missing_in_ach: []
  });

  const InvoicesUploadUrl = "uploads/csvUpload?type=achs";
  const SettlementsUploadUrl = "uploads/csvUpload?type=settlements";

  const handleFileChange = async (event, type) => {
    const file = event.target.files[0];
    if (!file) return;

    setSelectedFile(file);
    event.target.value = null; // Reset input

    try {
      const uploadUrl = type == "invoices" ? InvoicesUploadUrl : SettlementsUploadUrl;

      const formData = new FormData();
      formData.append("file", file);
      const response = await fetchMethodRequest("POST", uploadUrl, formData);

      if (response && response.respMessage) {
        if (type === "invoices") {
          setInvoiceFileUploaded(true);
        } else {
          setSettlementFileUploaded(true);
        }
        showToasterMessage?.("File processed successfully!", "success");
      } else {
        throw new Error("Processing failed");
      }
    } catch (err) {
      console.error("Single-file upload error:", err);
      if (toast.current) {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to process file. Please try again.",
          life: 5000,
        });
      }
    }
  };

  // Process and store data by match type
  const processMatchData = (jsonRes) => {
    if (!jsonRes?.resultSummary?.results) return null;

    const results = jsonRes.resultSummary.results;

    const processedData = {
      matched_1_1: [],
      matched_1_many: [],
      matched_many_1: [],
      matched_many_many: [],
      missing_in_settlement: results.missing_in_settlement || [],
      missing_in_ach: results.missing_in_ach || []
    };

    // Process 1:1 matches
    if (results.matched_1_1 && results.matched_1_1.length) {
      processedData.matched_1_1 = results.matched_1_1.map(item => ({
        refNo: item.refNo,
        // Settlement fields
        settlementTraceNumber: item.settlement?.traceNumber,
        settlementAmount: item.settlement?.amount,
        settlementDate: item.settlement?.date,
        settlementPayerName: item.settlement?.payerName,
        settlementPaymentType: item.settlement?.paymentType,
        settlementId: item.settlement?.settlementId,
        settlementRosilId: item.settlement?.rosiId,
        settlementPqasId: item.settlement?.pqasId,
        // ACH fields
        invoiceId: item.ach?.invoiceId,
        achAmount: item.ach?.amount,
        achDate: item.ach?.date,
        state: item.ach?.state,
        stateProgram: item.ach?.stateProgram,
        unitsRebated: item.ach?.unitsRebated,
        claimPeriod: item.ach?.claimPeriod,
        medicaidCycle: item.ach?.medicaidCycle
      }));
    }

    // Process 1:many matches
    if (results.matched_1_many && results.matched_1_many.length) {
      results.matched_1_many.forEach(item => {
        item.achs?.forEach(ach => {
          processedData.matched_1_many.push({
            refNo: item.refNo,
            // Settlement fields
            settlementTraceNumber: item.settlement?.traceNumber,
            settlementAmount: item.settlement?.amount,
            settlementDate: item.settlement?.date,
            settlementPayerName: item.settlement?.payerName,
            settlementPaymentType: item.settlement?.paymentType,
            settlementId: item.settlement?.settlementId,
            settlementRosilId: item.settlement?.rosiId,
            settlementPqasId: item.settlement?.pqasId,
            // ACH fields
            invoiceId: ach.invoiceId,
            achAmount: ach.amount,
            achDate: ach.date,
            state: ach.state,
            stateProgram: ach.stateProgram,
            unitsRebated: ach.unitsRebated,
            claimPeriod: ach.claimPeriod,
            medicaidCycle: ach.medicaidCycle
          });
        });
      });
    }

    // Process many:1 matches
    if (results.matched_many_1 && results.matched_many_1.length) {
      results.matched_many_1.forEach(item => {
        item.settlements?.forEach(settlement => {
          processedData.matched_many_1.push({
            refNo: item.refNo,
            // Settlement fields
            settlementTraceNumber: settlement.traceNumber,
            settlementAmount: settlement.amount,
            settlementDate: settlement.date,
            settlementPayerName: settlement.payerName,
            settlementPaymentType: settlement.paymentType,
            settlementId: settlement.settlementId,
            settlementRosilId: settlement.rosiId,
            settlementPqasId: settlement.pqasId,
            // ACH fields
            invoiceId: item.ach?.invoiceId,
            achAmount: item.ach?.amount,
            achDate: item.ach?.date,
            state: item.ach?.state,
            stateProgram: item.ach?.stateProgram,
            unitsRebated: item.ach?.unitsRebated,
            claimPeriod: item.ach?.claimPeriod,
            medicaidCycle: item.ach?.medicaidCycle
          });
        });
      });
    }

    // Process many:many matches
    if (results.matched_many_many && results.matched_many_many.length) {
      results.matched_many_many.forEach(item => {
        item.settlements?.forEach(settlement => {
          item.achs?.forEach(ach => {
            processedData.matched_many_many.push({
              refNo: item.refNo,
              // Settlement fields
              settlementTraceNumber: settlement.traceNumber,
              settlementAmount: settlement.amount,
              settlementDate: settlement.date,
              settlementPayerName: settlement.payerName,
              settlementPaymentType: settlement.paymentType,
              settlementId: settlement.settlementId,
              settlementRosilId: settlement.rosiId,
              settlementPqasId: settlement.pqasId,
              // ACH fields
              invoiceId: ach.invoiceId,
              achAmount: ach.amount,
              achDate: ach.date,
              state: ach.state,
              stateProgram: ach.stateProgram,
              unitsRebated: ach.unitsRebated,
              claimPeriod: ach.claimPeriod,
              medicaidCycle: ach.medicaidCycle
            });
          });
        });
      });
    }

    // Process missing in settlement
    if (results.missing_in_settlement && results.missing_in_settlement.length) {
      processedData.missing_in_settlement = results.missing_in_settlement.map(item => ({
        invoiceId: item.invoiceId,
        refNo: item.refNo,
        amount: item.amount,
        date: item.date,
        state: item.state,
        stateProgram: item.stateProgram,
        unitsRebated: item.unitsRebated,
        claimPeriod: item.claimPeriod,
        medicaidCycle: item.medicaidCycle
      }));
    }

    // Process missing in ACH
    if (results.missing_in_ach && results.missing_in_ach.length) {
      processedData.missing_in_ach = results.missing_in_ach.map(item => ({
        traceNumber: item.traceNumber,
        refNo: item.refNo,
        amount: item.amount,
        date: item.date,
        payerName: item.payerName,
        paymentType: item.paymentType,
        settlementId: item.settlementId,
        rosiId: item.rosiId,
        pqasId: item.pqasId,
        rebateProgram: item.rebateProgram
      }));
    }

    return processedData;
  };

  // Load matched API data
  const fetchMatchedData = async () => {
    try {
      console.log("Fetching matched data...");

      const jsonRes = await fetchMethodRequest("GET", "matches/runMatch")
      console.log("Response received", jsonRes);
      if (jsonRes.filePath) {
        setFilePath(jsonRes.filePath);
      }
      // Check if we have results
      if (jsonRes?.resultSummary?.results) {
        const results = jsonRes.resultSummary.results;

        // Process and store data by match type
        const processedData = processMatchData(jsonRes);
        setMatchData(processedData);

        // Combine all matched records for main table
        const allMatchedRecords = [];

        // Add 1:1 matches
        if (results.matched_1_1 && results.matched_1_1.length) {
          results.matched_1_1.forEach(item => {
            const flattened = {
              matchType: "1:1",
              refNo: item.refNo,
              // Settlement fields
              ...item.settlement,
              // ACH/Invoice fields
              invoiceId: item.ach?.invoiceId,
              achAmount: item.ach?.amount,
              achDate: item.ach?.date,
              state: item.ach?.state,
              stateProgram: item.ach?.stateProgram,
              unitsRebated: item.ach?.unitsRebated,
              claimPeriod: item.ach?.claimPeriod,
              medicaidCycle: item.ach?.medicaidCycle
            };
            allMatchedRecords.push(flattened);
          });
        }

        // Add 1:many matches
        if (results.matched_1_many && results.matched_1_many.length) {
          results.matched_1_many.forEach(item => {
            item.achs?.forEach(ach => {
              const flattened = {
                matchType: "1:many",
                refNo: item.refNo,
                // Settlement fields
                ...item.settlement,
                // ACH/Invoice fields
                invoiceId: ach.invoiceId,
                achAmount: ach.amount,
                achDate: ach.date,
                state: ach.state,
                stateProgram: ach.stateProgram,
                unitsRebated: ach.unitsRebated,
                claimPeriod: ach.claimPeriod,
                medicaidCycle: ach.medicaidCycle
              };
              allMatchedRecords.push(flattened);
            });
          });
        }

        // Add many:1 matches
        if (results.matched_many_1 && results.matched_many_1.length) {
          results.matched_many_1.forEach(item => {
            item.settlements?.forEach(settlement => {
              const flattened = {
                matchType: "many:1",
                refNo: item.refNo,
                // Settlement fields
                ...settlement,
                // ACH/Invoice fields
                invoiceId: item.ach?.invoiceId,
                achAmount: item.ach?.amount,
                achDate: item.ach?.date,
                state: item.ach?.state,
                stateProgram: item.ach?.stateProgram,
                unitsRebated: item.ach?.unitsRebated,
                claimPeriod: item.ach?.claimPeriod,
                medicaidCycle: item.ach?.medicaidCycle
              };
              allMatchedRecords.push(flattened);
            });
          });
        }

        // Add many:many matches
        if (results.matched_many_many && results.matched_many_many.length) {
          results.matched_many_many.forEach(item => {
            item.settlements?.forEach(settlement => {
              item.achs?.forEach(ach => {
                const flattened = {
                  matchType: "many:many",
                  refNo: item.refNo,
                  // Settlement fields
                  ...settlement,
                  // ACH/Invoice fields
                  invoiceId: ach.invoiceId,
                  achAmount: ach.amount,
                  achDate: ach.date,
                  state: ach.state,
                  stateProgram: ach.stateProgram,
                  unitsRebated: ach.unitsRebated,
                  claimPeriod: ach.claimPeriod,
                  medicaidCycle: ach.medicaidCycle
                };
                allMatchedRecords.push(flattened);
              });
            });
          });
        }

        if (allMatchedRecords.length > 0) {
          setApiData(allMatchedRecords);
          setApiColumns(['matchType', ...Object.keys(allMatchedRecords[0]).filter(k => k !== 'matchType')]);

          if (toast.current) {
            toast.current.show({
              severity: "success",
              summary: "API Loaded",
              detail: `${allMatchedRecords.length} matched records loaded`,
              life: 2000,
            });
          }

          console.log(`Loaded ${allMatchedRecords.length} matched records`);
        } else {
          if (toast.current) {
            toast.current.show({
              severity: "warn",
              summary: "No Records",
              detail: "No matched rows found in API response",
            });
          }
        }
      } else {
        if (toast.current) {
          toast.current.show({
            severity: "warn",
            summary: "No Records",
            detail: "No matched rows in API response",
          });
        }
      }
    } catch (e) {
      console.error("API Error:", e);
      if (toast.current) {
        toast.current.show({
          severity: "error",
          summary: "API Error",
          detail: "Failed to fetch matched data: " + (e.message || "Unknown error"),
        });
      }
    }
  };

  // View handlers for each match type
  const handleViewMatchType = (type) => {
    let title = "";
    switch (type) {
      case "matched_1_1":
        title = "1:1 Matched Records";
        break;
      case "matched_1_many":
        title = "1:Many Matched Records (One Settlement to Multiple Invoices)";
        break;
      case "matched_many_1":
        title = "Many:1 Matched Records (Multiple Settlements to One Invoice)";
        break;
      case "matched_many_many":
        title = "Many:Many Matched Records (Multiple Settlements to Multiple Invoices)";
        break;
      case "missing_in_settlement":
        title = "Missing in Settlement Records (Invoices with no matching Settlement)";
        break;
      case "missing_in_ach":
        title = "Missing in ACH/Invoice Records (Settlements with no matching Invoice)";
        break;
      default:
        title = "Match Records";
    }

    setPreviewTitle(title);
    setPreviewData(matchData[type] || []);
    setPreviewColumns(matchData[type]?.length ? Object.keys(matchData[type][0]) : []);
    setShowPreviewDialog(true);
  };

  const flattenObject = (obj, prefix = "") => {
    const flattened = {};
    for (const k in obj) {
      if (!Object.prototype.hasOwnProperty.call(obj, k)) continue;
      const val = obj[k];
      const key = prefix ? `${prefix}.${k}` : k;

      if (val && typeof val === "object" && !Array.isArray(val) && !(val instanceof Date)) {
        Object.assign(flattened, flattenObject(val, key));
      } else if (Array.isArray(val)) {
        flattened[key] = val
          .map((it) => (it && typeof it === "object" ? JSON.stringify(it) : String(it)))
          .join(", ");
      } else if (val instanceof Date) {
        flattened[key] = val.toISOString();
      } else {
        flattened[key] = val !== undefined && val !== null ? val : "";
      }
    }
    return flattened;
  };

  const exportJsonToExcel = (data, fileName = "export") => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      if (toast.current) {
        toast.current.show({
          severity: "warn",
          summary: "Nothing to export",
          detail: "No records available to download",
        });
      }
      return;
    }

    const flattenedRows = data.map((row) => flattenObject(row));
    const columnSet = new Set();

    flattenedRows.forEach((r) => {
      Object.keys(r).forEach((k) => columnSet.add(k));
    });

    const columns = Array.from(columnSet);
    const worksheetData = flattenedRows.map((r) => {
      const ordered = {};
      columns.forEach((c) => {
        ordered[c] = r[c] !== undefined ? r[c] : "";
      });
      return ordered;
    });

    const ws = XLSX.utils.json_to_sheet(worksheetData, { header: columns });
    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    XLSX.writeFile(wb, `${fileName}.xlsx`);

    if (toast.current) {
      toast.current.show({
        severity: "success",
        summary: "Download started",
        detail: `Saved ${fileName}.xlsx`,
        life: 1800,
      });
    }
  };

  const handleDownloadMatched = () => exportJsonToExcel(apiData, "matched_records");
  const handleDownloadMatchType = (type, fileName) => exportJsonToExcel(matchData[type] || [], fileName);

  const downloadExcel = () => {
    const fileUrl = filePath;

    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = filePath.split("/").pop(); // file name
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };


  useEffect(() => {
    const prev = localStorage.getItem("previous_excel");
    if (prev) setPreviousData(JSON.parse(prev));

    const curr = localStorage.getItem("current_excel");
    if (curr) setCurrentData(JSON.parse(curr));
  }, []);

  return (
    <div style={{ maxHeight: "90vh", overflowY: "auto", paddingRight: 10 }}>
      <Toast ref={toast} />

      {/* Hidden file inputs */}
      <input
        type="file"
        ref={invoicesFileInputRef}
        style={{ display: "none" }}
        onChange={(e) => handleFileChange(e, "invoices")}
        accept=".csv,.xlsx,.xls"
      />
      <input
        type="file"
        ref={settlementsFileInputRef}
        style={{ display: "none" }}
        onChange={(e) => handleFileChange(e, "settlements")}
        accept=".csv,.xlsx,.xls"
      />

      <div style={styles.wrapper}>

        {/* HEADER SECTION */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "white",
            padding: "15px",
            borderRadius: "10px",
            marginBottom: "12px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.06)",
            gap: "20px",
          }}
        >
          <h2 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>
            Payment Matching, Settlement Reconciliation & ROSI/PQAS Automation
          </h2>
          <i
            className="pi pi-eye"
            style={{
              fontSize: "22px",
              cursor: "pointer",
              color: "#1e3a8a",
            }}
            onClick={() => setShowHeadingDialog(true)}
          ></i>
        </div>

        {/* UPLOAD SECTION */}
        <div style={styles.uploadBox}>
          <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
            <button
              style={styles.apiBtn}
              onClick={() => invoicesFileInputRef.current.click()}
              disabled={invoiceFileUploaded}
            >
              Source 1 (Invoice) {invoiceFileUploaded && <i className="pi pi-check" style={{ marginLeft: 5 }}></i>}
            </button>

            <button
              style={styles.apiBtn}
              onClick={() => settlementsFileInputRef.current.click()}
              disabled={settlementFileUploaded}
            >
              Source 2 (Settlement) {settlementFileUploaded && <i className="pi pi-check" style={{ marginLeft: 5 }}></i>}
            </button>

            <button style={styles.apiBtn} onClick={fetchMatchedData}>
              Load & Process Matched Data
            </button>

            <button style={styles.apiBtn} onClick={downloadExcel}>
              Download All cases
            </button>
          </div>
        </div>

        {/* MATCH TYPE VIEW BUTTONS SECTION */}
        <div style={styles.matchTypeContainer}>
          <h3 style={{ margin: "0 0 15px 0", color: "#12225f" }}>Match Type Views</h3>

          <div style={styles.matchTypeGrid}>
            {/* 1:1 Matches */}
            <div style={styles.matchTypeCard}>
              <div style={styles.matchTypeHeader}>1:1 Matches</div>
              <div style={styles.matchTypeContent}>
                <span style={styles.matchCount}>{matchData.matched_1_1?.length || 0} records</span>
                <div style={styles.buttonGroup}>
                  <button
                    style={styles.viewButton}
                    onClick={() => handleViewMatchType("matched_1_1")}
                    disabled={!matchData.matched_1_1?.length}
                  >
                    <i className="pi pi-eye" style={{ marginRight: 5 }}></i>
                    View
                  </button>
                  <button
                    style={styles.downloadButton}
                    onClick={() => handleDownloadMatchType("matched_1_1", "1_1_matched_records")}
                    disabled={!matchData.matched_1_1?.length}
                  >
                    <i className="pi pi-download" style={{ marginRight: 5 }}></i>
                    Excel
                  </button>
                </div>
              </div>
            </div>

            {/* 1:Many Matches */}
            <div style={styles.matchTypeCard}>
              <div style={styles.matchTypeHeader}>1:Many Matches</div>
              <div style={styles.matchTypeContent}>
                <span style={styles.matchCount}>{matchData.matched_1_many?.length || 0} records</span>
                <div style={styles.buttonGroup}>
                  <button
                    style={styles.viewButton}
                    onClick={() => handleViewMatchType("matched_1_many")}
                    disabled={!matchData.matched_1_many?.length}
                  >
                    <i className="pi pi-eye" style={{ marginRight: 5 }}></i>
                    View
                  </button>
                  <button
                    style={styles.downloadButton}
                    onClick={() => handleDownloadMatchType("matched_1_many", "1_many_matched_records")}
                    disabled={!matchData.matched_1_many?.length}
                  >
                    <i className="pi pi-download" style={{ marginRight: 5 }}></i>
                    Excel
                  </button>
                </div>
              </div>
            </div>

            {/* Many:1 Matches */}
            <div style={styles.matchTypeCard}>
              <div style={styles.matchTypeHeader}>Many:1 Matches</div>
              <div style={styles.matchTypeContent}>
                <span style={styles.matchCount}>{matchData.matched_many_1?.length || 0} records</span>
                <div style={styles.buttonGroup}>
                  <button
                    style={styles.viewButton}
                    onClick={() => handleViewMatchType("matched_many_1")}
                    disabled={!matchData.matched_many_1?.length}
                  >
                    <i className="pi pi-eye" style={{ marginRight: 5 }}></i>
                    View
                  </button>
                  <button
                    style={styles.downloadButton}
                    onClick={() => handleDownloadMatchType("matched_many_1", "many_1_matched_records")}
                    disabled={!matchData.matched_many_1?.length}
                  >
                    <i className="pi pi-download" style={{ marginRight: 5 }}></i>
                    Excel
                  </button>
                </div>
              </div>
            </div>

            {/* Many:Many Matches */}
            <div style={styles.matchTypeCard}>
              <div style={styles.matchTypeHeader}>Many:Many Matches</div>
              <div style={styles.matchTypeContent}>
                <span style={styles.matchCount}>{matchData.matched_many_many?.length || 0} records</span>
                <div style={styles.buttonGroup}>
                  <button
                    style={styles.viewButton}
                    onClick={() => handleViewMatchType("matched_many_many")}
                    disabled={!matchData.matched_many_many?.length}
                  >
                    <i className="pi pi-eye" style={{ marginRight: 5 }}></i>
                    View
                  </button>
                  <button
                    style={styles.downloadButton}
                    onClick={() => handleDownloadMatchType("matched_many_many", "many_many_matched_records")}
                    disabled={!matchData.matched_many_many?.length}
                  >
                    <i className="pi pi-download" style={{ marginRight: 5 }}></i>
                    Excel
                  </button>
                </div>
              </div>
            </div>

            {/* Missing in Settlement */}
            <div style={{ ...styles.matchTypeCard, borderTop: "4px solid #e53935" }}>
              <div style={{ ...styles.matchTypeHeader, background: "#ffebee", color: "#c62828" }}>Missing in Settlement</div>
              <div style={styles.matchTypeContent}>
                <span style={styles.matchCount}>{matchData.missing_in_settlement?.length || 0} records</span>
                <div style={styles.buttonGroup}>
                  <button
                    style={styles.viewButton}
                    onClick={() => handleViewMatchType("missing_in_settlement")}
                    disabled={!matchData.missing_in_settlement?.length}
                  >
                    <i className="pi pi-eye" style={{ marginRight: 5 }}></i>
                    View
                  </button>
                  <button
                    style={styles.downloadButton}
                    onClick={() => handleDownloadMatchType("missing_in_settlement", "missing_in_settlement")}
                    disabled={!matchData.missing_in_settlement?.length}
                  >
                    <i className="pi pi-download" style={{ marginRight: 5 }}></i>
                    Excel
                  </button>
                </div>
              </div>
            </div>

            {/* Missing in ACH */}
            <div style={{ ...styles.matchTypeCard, borderTop: "4px solid #e53935" }}>
              <div style={{ ...styles.matchTypeHeader, background: "#ffebee", color: "#c62828" }}>Missing in ACH/Invoice</div>
              <div style={styles.matchTypeContent}>
                <span style={styles.matchCount}>{matchData.missing_in_ach?.length || 0} records</span>
                <div style={styles.buttonGroup}>
                  <button
                    style={styles.viewButton}
                    onClick={() => handleViewMatchType("missing_in_ach")}
                    disabled={!matchData.missing_in_ach?.length}
                  >
                    <i className="pi pi-eye" style={{ marginRight: 5 }}></i>
                    View
                  </button>
                  <button
                    style={styles.downloadButton}
                    onClick={() => handleDownloadMatchType("missing_in_ach", "missing_in_ach")}
                    disabled={!matchData.missing_in_ach?.length}
                  >
                    <i className="pi pi-download" style={{ marginRight: 5 }}></i>
                    Excel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>


        {/* PREVIEW DIALOG */}
        <Dialog
          header={previewTitle}
          visible={showPreviewDialog}
          style={{ width: "95vw", maxHeight: "100vh" }}
          maximizable
          onHide={() => setShowPreviewDialog(false)}
        >

          {previewData.length > 0 ? (
            <div style={{ marginTop: "20px" }}>
              <DataTable
                value={previewData}
                showGridlines
                stripedRows
                className="p-datatable-sm"
                paginator
                filter
                rows={30}
              >
                <Column
                  header="S.No"
                  body={(rowData, options) => options.rowIndex + 1}
                />
                {previewColumns.map((col) => (
                  <Column
                    key={col}
                    field={col}
                    header={col.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                    style={{ fontSize: "12px", minWidth: "120px" }}
                  />
                ))}
              </DataTable>
            </div>
          ) : (
            <div style={{ textAlign: "center", padding: "50px", color: "#666" }}>
              <i className="pi pi-info-circle" style={{ fontSize: "48px", marginBottom: "20px" }}></i>
              <h3>No records available</h3>
              <p>Please load matched data first using the "Load & Process Matched Data" button.</p>
            </div>
          )}
        </Dialog>


        <Dialog
          header="Payment Documentation — Details"
          visible={showHeadingDialog}
          style={{ width: "60vw" }}
          onHide={() => setShowHeadingDialog(false)}
        >
          <div style={{ lineHeight: 1.6, fontSize: "15px" }}>

            <p><strong>Tracking #:</strong> 30</p>
            <p><strong>Area:</strong> Medicaid</p>
            <p><strong>Contact:</strong> Janet Berry</p>

            <p style={{ marginTop: 10 }}>
              Match the checks/ACHs to the settlement, pull the ROSI/PQAS then email payment
              and backup documentation to the states and third parties.
            </p>

            <ul>
              <li>AI can match different documents and generate the documents needed</li>
              <li>Agent reads 2 Excel files, compares matching fields, updates tracker</li>
              <li>LLM + OCR based automation</li>
              <li>Saves ~80 hours per month</li>
              <li>Production Ready: Yes</li>
            </ul>

            <hr />
          </div>
          <div style={{ marginTop: 20 }}>
            <h4 style={{ marginBottom: 10 }}>Process</h4>

            <ol style={{ paddingLeft: 20, lineHeight: "1.6" }}>
              <li><strong>Directly click the "Load Matched Data Button to See the Matching Records in two Sources (1&2)"</strong></li>

              <li><strong>click "Download Matched as Excel""</strong></li>

            </ol>
          </div>
        </Dialog>
      </div>
    </div>
  );
}

const styles = {
  wrapper: {
    margin: "5px",
    padding: "5px",
    background: "#f3f4f6",
  },

  uploadBox: {
    padding: 16,
    background: "white",
    borderRadius: 8,
    textAlign: "center",
    marginBottom: "20px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.06)",
  },

  matchTypeContainer: {
    padding: 16,
    background: "white",
    borderRadius: 8,
    marginBottom: "20px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.06)",
  },

  matchTypeGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: "16px",
  },

  matchTypeCard: {
    background: "#fff",
    border: "1px solid #e0e0e0",
    borderRadius: "8px",
    overflow: "hidden",
    transition: "all 0.3s ease",
    borderTop: "4px solid #12225f",
    boxShadow: "0 2px 4px rgba(0,0,0,0.04)",
  },

  matchTypeHeader: {
    background: "#f8f9fa",
    padding: "12px 16px",
    fontWeight: 600,
    color: "#12225f",
    borderBottom: "1px solid #e0e0e0",
    fontSize: "15px",
  },

  matchTypeContent: {
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },

  matchCount: {
    fontSize: "24px",
    fontWeight: 700,
    color: "#12225f",
  },

  buttonGroup: {
    display: "flex",
    gap: "8px",
  },

  viewButton: {
    flex: 1,
    background: "#12225f",
    color: "white",
    border: "none",
    padding: "8px 12px",
    borderRadius: "4px",
    cursor: "pointer",
    fontWeight: 500,
    fontSize: "13px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background 0.3s ease",
    opacity: 1,
    ":hover": {
      background: "#0b1845",
    },
    ":disabled": {
      background: "#ccc",
      cursor: "not-allowed",
    }
  },

  downloadButton: {
    flex: 1,
    background: "#0b5f3b",
    color: "white",
    border: "none",
    padding: "8px 12px",
    borderRadius: "4px",
    cursor: "pointer",
    fontWeight: 500,
    fontSize: "13px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background 0.3s ease",
    ":hover": {
      background: "#084a2f",
    },
    ":disabled": {
      background: "#ccc",
      cursor: "not-allowed",
    }
  },

  apiBtn: {
    background: "#12225f",
    padding: "10px 18px",
    borderRadius: 6,
    color: "white",
    fontWeight: 600,
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "14px",
  },

  mainTableContainer: {
    padding: 16,
    background: "white",
    borderRadius: 8,
    boxShadow: "0 2px 4px rgba(0,0,0,0.06)",
  },
};